/**
 * Status pills for the Campaign Reports tab — campaign page status + pacing
 * (moved as-is from the original tab), GAM order lifecycle, and advertiser
 * sync state. Every label is explicit text.
 */

import React from 'react';
import { PACING_PILL, StatusPill } from '../../ui';
import type { AdvertiserCampaignLifecycle, CampaignReportPage } from '../../../services/campaignReportsApi';
import type { AdvertiserReportPage } from '../../../services/advertiserReportsApi';
import { LIFECYCLE_PILL, campaignRefreshState, describeSync } from './shared';

// NOTE: passcode lockout is keyed per (slug, client-IP) in the
// campaignReportPinAttempts collection, NOT on the page doc — there is no single
// "page is locked" state to surface here. (A stale page-doc lockedUntil from
// before that change would otherwise render a permanent phantom "Locked" pill.)

export const PageStatus: React.FC<{ page: CampaignReportPage }> = ({ page }) => {
  const refresh = campaignRefreshState(page);
  return (
  <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
    {page.revokedAt ? (
      <StatusPill variant="paused">Revoked</StatusPill>
    ) : page.expired ? (
      <StatusPill variant="draft">Expired</StatusPill>
    ) : page.dataStatus === 'generating' ? (
      <StatusPill variant="pending">Generating…</StatusPill>
    ) : page.dataStatus === 'pending' ? (
      <span title="No complete flight day yet — the report generates automatically after the campaign's first full day">
        <StatusPill variant="pending">Awaiting data</StatusPill>
      </span>
    ) : page.dataStatus === 'error' ? (
      <span title={page.dataError || 'Generation failed'}>
        <StatusPill variant="flagged">Error</StatusPill>
      </span>
    ) : refresh.phase === 'final' ? (
      <StatusPill variant="approved">Final</StatusPill>
    ) : refresh.phase === 'settling' ? (
      <span title="Delivery is complete in GAM (or the reporting end has passed); figures keep refreshing for 14 days after the reporting end"><StatusPill variant="pending">Settling</StatusPill></span>
    ) : (
      <StatusPill variant="ok">Live</StatusPill>
    )}
    <PacingPill page={page} />
    {refresh.problem && <span title={refresh.detail}><StatusPill variant="flagged">{refresh.problem === 'error' ? 'Refresh error' : 'Refresh overdue'}</StatusPill></span>}
    {!page.autoRefresh && !page.revokedAt && <StatusPill variant="paused">Auto-refresh off</StatusPill>}
    {page.hideBookedGoal && !page.revokedAt && (
      <span title="The client page, XLSX and hub card show delivered impressions only — no booked goal, no pacing verdict (Edit to change)">
        <StatusPill variant="draft">Goal hidden</StatusPill>
      </span>
    )}
  </span>
  );
};

/**
 * Delivery-pacing pill from the page doc's last pacing capture. Shown while
 * the order's flight is still running — including on finalized month-slice
 * pages, which the hourly pacing refresh keeps current (the tooltip dates the
 * capture). Ended flights, NOT_STARTED/COMPLETED, and unavailable pacing
 * (sponsorship-only orders, fetch failures) render nothing. Variant/label come
 * from the shared PACING_PILL map so this list matches Current Campaigns.
 */
export const PacingPill: React.FC<{ page: CampaignReportPage }> = ({ page }) => {
  if (page.revokedAt || page.expired || page.dataStatus !== 'ready' || !page.pacingStatus) return null;
  if (page.pacingStatus === 'NOT_STARTED' || page.pacingStatus === 'COMPLETED') return null;
  // Flight over (LA calendar) → the frozen status is history, not a signal.
  const todayLa = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  if (page.flightEndDate < todayLa) return null;
  const pill = PACING_PILL[page.pacingStatus];
  // pacingAsOf is the capture moment (hourly refresh); pages last written
  // before it existed fall back to the finalized-era lastRefreshAt date.
  const asOfIso = page.pacingAsOf ?? (page.finalized ? page.lastRefreshAt : undefined);
  const asOf = asOfIso
    ? ` as of ${new Date(asOfIso).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })} PT`
    : '';
  const pct =
    page.pacingProgressPercent != null
      ? `${Math.round(page.pacingProgressPercent)}% of expected pace${asOf}`
      : undefined;
  return (
    <span title={pct}>
      <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
    </span>
  );
};

/** GAM order lifecycle pill (Delivering / Upcoming / Paused / In work / Ended). null → "—". */
export const LifecyclePill: React.FC<{ lifecycle: AdvertiserCampaignLifecycle | null | undefined }> = ({ lifecycle }) => {
  if (!lifecycle) return <span style={{ color: 'var(--fg-4)' }}>—</span>;
  const pill = LIFECYCLE_PILL[lifecycle];
  return <StatusPill variant={pill.variant}>{pill.label}</StatusPill>;
};

/** Advertiser hub sync state (Revoked pill first when the page is revoked). */
export const SyncPill: React.FC<{ page: AdvertiserReportPage }> = ({ page }) => {
  const spec = describeSync(page);
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {page.revokedAt && <StatusPill variant="paused">Revoked</StatusPill>}
      <span title={spec.title}>
        <StatusPill variant={spec.variant}>{spec.label}</StatusPill>
      </span>
    </span>
  );
};
